// UCs do mesmo cliente numa parada só.
//
// Pedido do Vinicius em 16/09/2026, olhando a rota "Cobrança geral": o Max
// Emiliano tinha duas UCs em débito e a rota mandava o cobrador bater na mesma
// porta duas vezes. "Faz mais sentido ir só em uma e já cobrar lá o montante das
// UCs que estão em dívida." Naquela rota eram cinco clientes assim — Super Gelo e
// Eurivan com três paradas cada — e 34 paradas viravam 26.
//
// A parada continua tendo UMA UC principal: a do endereço onde se vai. As outras
// do mesmo CPF/CNPJ entram em `ucs_agrupadas` e somam no valor a cobrar.
//
// Tudo aqui é puro, sem banco: a montagem (UcInadimplente) e a conferência
// (parada já salva) usam as mesmas regras.

/** Uma UC cobrada numa parada que não é a principal. Formato do jsonb `ucs_agrupadas`. */
export interface UcAgrupada {
    unidade_consumidora_id: number | null;
    uc_codigo: string;
    cliente_nome: string | null;
    qtd_faturas: number;
    valor_total: number;
}

/** CPF/CNPJ só com dígitos, ou null quando não serve para juntar.
 *  Menos de 11 dígitos é lixo de cadastro — juntar por ele colaria clientes diferentes. */
export const chaveCliente = (cpfCnpj: string | null | undefined): string | null => {
    const d = String(cpfCnpj ?? '').replace(/\D/g, '');
    return d.length >= 11 ? d : null;
};

const centavos = (v: number): number => Math.round((Number(v) || 0) * 100) / 100;

// ---------------------------------------------------------------------------
// Na montagem
// ---------------------------------------------------------------------------

/** O mínimo que a montagem precisa de uma UC para decidir o grupo. */
export interface UcAgrupavel {
    cpfCnpj: string | null;
    valorTotal: number;
    coordenadaConfiavel: boolean;
}

export interface GrupoCliente<T> {
    principal: T;
    extras: T[];
}

/**
 * Qual UC do grupo é a parada.
 *
 * Primeiro a que tem localização confiável — é a única que dá para ordenar e
 * navegar; escolher a outra jogaria o cliente inteiro para o fim da rota. Entre
 * as localizáveis, a de maior dívida: é onde a conversa vai acontecer.
 */
const escolherPrincipal = <T extends UcAgrupavel>(ucs: T[]): T =>
    [...ucs].sort((a, b) => {
        if (a.coordenadaConfiavel !== b.coordenadaConfiavel) return a.coordenadaConfiavel ? -1 : 1;
        return (Number(b.valorTotal) || 0) - (Number(a.valorTotal) || 0);
    })[0];

/**
 * Junta as UCs escolhidas por CPF/CNPJ.
 *
 * Mantém a ordem de chegada (a do primeiro aparecimento de cada cliente) e deixa
 * sozinha a UC sem documento: sem CPF não há como afirmar que é o mesmo cliente,
 * e nome igual não basta — há homônimos na base.
 */
export const agruparPorCliente = <T extends UcAgrupavel>(ucs: T[]): GrupoCliente<T>[] => {
    const porChave = new Map<string, T[]>();
    const ordem: Array<string | T> = [];
    (ucs || []).forEach(uc => {
        const chave = chaveCliente(uc.cpfCnpj);
        if (!chave) { ordem.push(uc); return; }
        const lista = porChave.get(chave);
        if (lista) { lista.push(uc); return; }
        porChave.set(chave, [uc]);
        ordem.push(chave);
    });

    return ordem.map(item => {
        if (typeof item !== 'string') return { principal: item, extras: [] };
        const lista = porChave.get(item)!;
        const principal = escolherPrincipal(lista);
        return { principal, extras: lista.filter(uc => uc !== principal) };
    });
};

/** Quantas paradas a junção economiza — para a tela dizer antes de gerar. */
export const paradasEconomizadas = <T extends UcAgrupavel>(ucs: T[]): number =>
    agruparPorCliente(ucs).reduce((s, g) => s + g.extras.length, 0);

// ---------------------------------------------------------------------------
// Na parada salva
// ---------------------------------------------------------------------------

/** O subconjunto de `rota_cobranca_paradas` que o agrupamento lê. */
export interface ParadaAgrupavel {
    id: string;
    posicao: number;
    unidade_consumidora_id: number | null;
    uc_codigo: string;
    cliente_nome: string | null;
    cpf_cnpj_cliente: string | null;
    qtd_faturas: number | null;
    valor_total: number | null;
    ucs_agrupadas?: UcAgrupada[] | null;
}

const agrupadasDe = (p: ParadaAgrupavel): UcAgrupada[] =>
    Array.isArray(p.ucs_agrupadas) ? p.ucs_agrupadas : [];

/**
 * Todas as UCs cobradas na parada, a principal primeiro.
 *
 * O valor da principal é derivado (total menos as agrupadas) porque a coluna da
 * parada guarda o total — é o número que o cobrador cobra na porta.
 */
export const ucsDaParada = (p: ParadaAgrupavel): UcAgrupada[] => {
    const extras = agrupadasDe(p);
    const somaValor = extras.reduce((s, u) => s + (Number(u.valor_total) || 0), 0);
    const somaQtd = extras.reduce((s, u) => s + (Number(u.qtd_faturas) || 0), 0);
    return [
        {
            unidade_consumidora_id: p.unidade_consumidora_id,
            uc_codigo: p.uc_codigo,
            cliente_nome: p.cliente_nome,
            qtd_faturas: Math.max(0, (Number(p.qtd_faturas) || 0) - somaQtd),
            valor_total: centavos(Math.max(0, (Number(p.valor_total) || 0) - somaValor)),
        },
        ...extras,
    ];
};

export const paradaAgrupada = (p: ParadaAgrupavel): boolean => agrupadasDe(p).length > 0;

/** Os campos da parada que mudam quando o conjunto de UCs muda. */
export interface CamposGrupo {
    unidade_consumidora_id: number | null;
    uc_codigo: string;
    cliente_nome: string | null;
    qtd_faturas: number;
    valor_total: number;
    ucs_agrupadas: UcAgrupada[] | null;
}

/** Monta a parada a partir de uma lista de UCs — a primeira vira a principal. */
export const camposDoGrupo = (ucs: UcAgrupada[]): CamposGrupo => {
    const [principal, ...extras] = ucs;
    return {
        unidade_consumidora_id: principal.unidade_consumidora_id,
        uc_codigo: principal.uc_codigo,
        cliente_nome: principal.cliente_nome,
        qtd_faturas: ucs.reduce((s, u) => s + (Number(u.qtd_faturas) || 0), 0),
        valor_total: centavos(ucs.reduce((s, u) => s + (Number(u.valor_total) || 0), 0)),
        ucs_agrupadas: extras.length > 0 ? extras : null,
    };
};

export type ResultadoTirarUc =
    /** Era a única UC: a parada inteira sai da rota. */
    | { tipo: 'apagar_parada' }
    /** A parada fica com as UCs restantes. `trocouEndereco` = saiu a principal,
     *  e o endereço conferido era dela. */
    | { tipo: 'atualizar'; campos: CamposGrupo; trocouEndereco: boolean };

/**
 * O que acontece com a parada quando uma UC sai dela.
 *
 * Tirar a principal promove a próxima — e o endereço conferido deixa de valer,
 * porque era o da UC que saiu.
 */
export const tirarUcDaParada = (p: ParadaAgrupavel, ucCodigo: string): ResultadoTirarUc => {
    const todas = ucsDaParada(p);
    const restantes = todas.filter(u => u.uc_codigo !== ucCodigo);
    if (restantes.length === todas.length) {
        throw new Error(`A UC ${ucCodigo} não está nesta parada.`);
    }
    if (restantes.length === 0) return { tipo: 'apagar_parada' };
    return {
        tipo: 'atualizar',
        campos: camposDoGrupo(restantes),
        trocouEndereco: todas[0].uc_codigo === ucCodigo,
    };
};

export interface JuncaoParadas {
    /** A parada que fica, com todas as UCs do cliente. */
    manter: ParadaAgrupavel;
    campos: CamposGrupo;
    /** As que somem — as UCs delas passaram para `manter`. */
    apagar: ParadaAgrupavel[];
}

/**
 * Paradas já salvas que são do mesmo cliente, e como juntá-las.
 *
 * Existe para as rotas montadas antes da junção automática — a de 34 paradas que
 * originou o pedido. Fica a parada que tem cadastro (é a que tem endereço para
 * conferir) e, entre elas, a de maior dívida; empate, a que vem antes na rota.
 */
export const juncoesPendentes = (paradas: ParadaAgrupavel[]): JuncaoParadas[] => {
    const porChave = new Map<string, ParadaAgrupavel[]>();
    [...(paradas || [])]
        .sort((a, b) => a.posicao - b.posicao)
        .forEach(p => {
            const chave = chaveCliente(p.cpf_cnpj_cliente);
            if (!chave) return;
            porChave.set(chave, [...(porChave.get(chave) ?? []), p]);
        });

    const juncoes: JuncaoParadas[] = [];
    porChave.forEach(lista => {
        if (lista.length < 2) return;
        const manter = [...lista].sort((a, b) => {
            const ca = a.unidade_consumidora_id != null ? 1 : 0;
            const cb = b.unidade_consumidora_id != null ? 1 : 0;
            if (ca !== cb) return cb - ca;
            const dv = (Number(b.valor_total) || 0) - (Number(a.valor_total) || 0);
            if (Math.abs(dv) > 0.005) return dv;
            return a.posicao - b.posicao;
        })[0];
        const apagar = lista.filter(p => p !== manter);
        const ucs = [
            ...ucsDaParada(manter),
            ...apagar.flatMap(ucsDaParada),
        ];
        juncoes.push({ manter, campos: camposDoGrupo(ucs), apagar });
    });
    return juncoes.sort((a, b) => a.manter.posicao - b.manter.posicao);
};

/**
 * Nova posição de cada parada depois de tirar alguma: 1, 2, 3... sem buracos,
 * na ordem que já estava. Devolve só as que mudam.
 *
 * O buraco não é só estético: o deep-link da mensagem abre `?parada=N`, e a lista
 * "ainda travam: paradas 2, 5, 7" apontaria para números que não existem.
 */
export const renumerar = (paradas: Array<{ id: string; posicao: number }>): Array<{ id: string; posicao: number }> =>
    [...paradas]
        .sort((a, b) => a.posicao - b.posicao)
        .map((p, i) => ({ id: p.id, posicao: i + 1, antes: p.posicao }))
        .filter(p => p.posicao !== p.antes)
        .map(({ id, posicao }) => ({ id, posicao }));

/**
 * Nova posição de cada parada quando o cobrador reordena a rota a partir de onde
 * está. Devolve só as que mudam.
 *
 * Quem já foi visitado ou pulado vai para o começo, na ordem em que estava: é o
 * que já aconteceu, e não faz sentido o otimizador mexer. Depois vêm as
 * pendentes na ordem que o otimizador devolveu e, no fim, as pendentes que ele
 * não recebeu (sem coordenada confiável), na ordem de antes — continuam na rota
 * para o cobrador decidir, como na montagem.
 */
export const posicoesAposReordenar = (
    paradas: Array<{ id: string; posicao: number; status: string }>,
    ordemOtimizada: string[],
): Array<{ id: string; posicao: number }> => {
    const atuais = [...paradas].sort((a, b) => a.posicao - b.posicao);
    const pendentes = new Set(atuais.filter(p => p.status === 'pendente').map(p => p.id));
    const otimizadas = ordemOtimizada.filter(id => pendentes.has(id));
    const jaOrdenadas = new Set(otimizadas);
    const sequencia = [
        ...atuais.filter(p => p.status !== 'pendente').map(p => p.id),
        ...otimizadas,
        ...atuais.filter(p => p.status === 'pendente' && !jaOrdenadas.has(p.id)).map(p => p.id),
    ];
    const antes = new Map(atuais.map(p => [p.id, p.posicao]));
    return sequencia
        .map((id, i) => ({ id, posicao: i + 1 }))
        .filter(p => antes.get(p.id) !== p.posicao);
};

/**
 * Nova posição de cada parada quando uma vai para o fim da fila — reagendada ou
 * reaberta. Devolve só as que mudam.
 *
 * Sem isto a parada reagendada "para mais tarde" continuava a primeira da
 * lista: a próxima porta e o link do Maps apontavam de novo para onde o cobrador
 * acabou de sair. As outras sobem uma posição, sem buraco na numeração.
 */
export const posicoesMandandoParaOFim = (
    paradas: Array<{ id: string; posicao: number }>,
    id: string,
): Array<{ id: string; posicao: number }> => {
    if (!paradas.some(p => p.id === id)) return [];
    const atuais = [...paradas].sort((a, b) => a.posicao - b.posicao);
    const sequencia = [...atuais.filter(p => p.id !== id).map(p => p.id), id];
    const antes = new Map(atuais.map(p => [p.id, p.posicao]));
    return sequencia
        .map((pid, i) => ({ id: pid, posicao: i + 1 }))
        .filter(p => antes.get(p.id) !== p.posicao);
};
